import React, { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../../app/components/ui/tabs";
import { api } from "../../../lib/api";
import { formatInrLoose, formatInrThousandsTick } from "../../../lib/currency";
import { useAuth } from "../../../providers/AuthProvider";

export function AdminSalesOverviewChart() {
  const [period, setPeriod] = useState<"weekly" | "monthly">("weekly");
  const { token } = useAuth();
  const [rows, setRows] = useState<Array<{ date: string; total_sales: string }>>([]);
  const [loading, setLoading] = useState(false);

  const dateRange = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);
    start.setDate(now.getDate() - (period === "weekly" ? 6 : 29));

    const toYmd = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    return { from_date: toYmd(start), to_date: toYmd(end) };
  }, [period]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    let cancelled = false;
    (async () => {
      try {
        const res = await api.adminReports.getSalesTrend({ token, filters: dateRange });
        if (cancelled) return;
        setRows((res.data as any[]) ?? []);
      } catch {
        if (cancelled) return;
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, dateRange]);

  const chartData = useMemo(() => {
    if (rows.length === 0) return [];

    const parse = (dateStr: string) => {
      // Safe parse for YYYY-MM-DD
      const [y, m, d] = dateStr.split("-").map((x) => Number(x));
      return new Date(y, m - 1, d);
    };

    if (period === "weekly") {
      return rows.map((r) => {
        const dt = parse(r.date);
        const label = dt.toLocaleDateString(undefined, { weekday: "short" });
        return { name: label, sales: Number(r.total_sales) };
      });
    }

    // Monthly: bucket by week index over the last 30 days into "Week 1".."Week 4"
    const start = parse(rows[0].date);
    const end = parse(rows[rows.length - 1].date);
    const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
    const bucketCount = 4;
    const bucketSizeDays = Math.max(1, Math.ceil((totalDays + 1) / bucketCount));

    const buckets: Record<string, number> = {};
    for (const r of rows) {
      const dt = parse(r.date);
      const dayIndex = Math.floor((dt.getTime() - start.getTime()) / 86400000);
      const bucketIdx = Math.min(bucketCount - 1, Math.floor(dayIndex / bucketSizeDays));
      const label = `Week ${bucketIdx + 1}`;
      buckets[label] = (buckets[label] ?? 0) + Number(r.total_sales);
    }

    return Object.entries(buckets)
      .map(([name, sales]) => ({ name, sales }))
      .sort((a, b) => {
        const ai = Number(a.name.replace("Week ", ""));
        const bi = Number(b.name.replace("Week ", ""));
        return ai - bi;
      });
  }, [period, rows]);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle>Platform sales overview</CardTitle>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as any)}>
            <TabsList>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatInrThousandsTick(Number(v))}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [formatInrLoose(value), "Sales"]}
              />
              <Bar dataKey="sales" fill="#2563eb" radius={[8, 8, 0, 0]} animationDuration={900} />
            </BarChart>
          </ResponsiveContainer>
          {loading ? <div className="text-sm text-muted-foreground mt-3">Loading chart...</div> : null}
        </CardContent>
      </Card>
    </motion.div>
  );
}

